import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, Legend
} from 'recharts';
import { TrendingUp, Users, DollarSign, ChevronRight, BarChart2, PieChart as PieChartIcon, TrendingDown, Target, Star } from 'lucide-react';
import TopNavbar from '../components/TopNavbar';
import MobileNavbar from '../components/MobileNavbar';
import Footer from '../components/Landing/Footer';
import { useNavigate } from 'react-router-dom';

// Sample chart data
const COLORS = ['#FF6C6C', '#FFA26C', '#FFD166', '#06D6A0', '#118AB2'];

const analyticsData = {
  monthlySales: [
    { month: 'Jan', value: 2400 },
    { month: 'Feb', value: 1398 },
    { month: 'Mar', value: 9800 },
    { month: 'Apr', value: 3908 },
    { month: 'May', value: 4800 },
    { month: 'Jun', value: 3800 },
    { month: 'Jul', value: 4300 },
    { month: 'Aug', value: 5300 },
    { month: 'Sep', value: 4800 },
    { month: 'Oct', value: 7800 },
    { month: 'Nov', value: 8900 },
    { month: 'Dec', value: 9000 },
  ],
  categoryDistribution: [
    { name: 'Electronics & Gadgets', value: 35 },
    { name: 'Fashion & Accessories', value: 25 },
    { name: 'Events & Entertainment', value: 20 },
    { name: 'Stationery', value: 15 },
    { name: 'Furniture', value: 5 },
  ],
  customerEngagement: [
    { month: 'Jan', views: 4000, redemptions: 2400, clicks: 3000 },
    { month: 'Feb', views: 3000, redemptions: 1398, clicks: 2000 },
    { month: 'Mar', views: 2000, redemptions: 800, clicks: 1500 },
    { month: 'Apr', views: 2780, redemptions: 1908, clicks: 2500 },
    { month: 'May', views: 1890, redemptions: 800, clicks: 1200 },
    { month: 'Jun', views: 2390, redemptions: 1800, clicks: 2100 },
    { month: 'Jul', views: 3490, redemptions: 2300, clicks: 3000 },
  ]
};

const analyticsFeatures = [
  {
    title: "Revenue Tracking",
    icon: <DollarSign className="text-[#FF6C6C]" />,
    description: "Track all revenue generated through deals, with detailed breakdowns by campaign, location, and time period."
  },
  {
    title: "Customer Insights",
    icon: <Users className="text-[#FF6C6C]" />,
    description: "Understand your customer demographics and behaviors to optimize your marketing strategies."
  },
  {
    title: "Performance Metrics",
    icon: <BarChart2 className="text-[#FF6C6C]" />,
    description: "Monitor key performance indicators with intuitive dashboards that highlight what matters most."
  },
  {
    title: "Conversion Analytics",
    icon: <TrendingUp className="text-[#FF6C6C]" />,
    description: "See exactly how many views convert to sales, and identify optimization opportunities."
  },
  {
    title: "Category Breakdown",
    icon: <PieChartIcon className="text-[#FF6C6C]" />,
    description: "Analyze which product categories or service types perform best with detailed segmentation."
  },
  {
    title: "Trend Analysis",
    icon: <TrendingDown className="text-[#FF6C6C]" />,
    description: "Identify patterns and trends over time to make data-driven decisions about your business."
  }
];

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <TopNavbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      {isMenuOpen && (
        <MobileNavbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      )}
      
      {/* Hero Section */}
      <section className="overflow-hidden pt-28 pb-16">
        <div className="container px-4 mx-auto">
          <div className="flex flex-wrap items-center -mx-4">
            <div className="px-4 mb-16 w-full md:w-1/2 md:mb-0">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-xl"
              >
                <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                  Make <span className="text-[#FF6C6C]">Data-Driven</span> Decisions for Your Business
                </h1>
                <p className="mb-8 text-lg text-gray-600">
                  Our powerful analytics platform gives you the insights you need to understand your customers,
                  optimize your marketing, and grow your revenue.
                </p>
                <div className="flex flex-wrap gap-4">
                  <motion.button
                  onClick={()=>navigate("/signin")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 font-medium text-white transition-all rounded-lg bg-gradient-to-r from-[#FF6C6C] to-[#FF8F6C] hover:shadow-lg"
                  >
                    Get Started
                  </motion.button>
                  <button onClick={()=>navigate("/support")} className="flex items-center px-8 py-4 font-medium transition-all rounded-lg text-[#FF6C6C] hover:bg-[#FF6C6C]/5">
                    Contact Us
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            </div>
            <div className="px-4 w-full md:w-1/2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="overflow-hidden relative z-10 bg-white rounded-xl shadow-xl">
                  <div className="p-2">
                    <div className="h-[340px]">
                      <ResponsiveContainer width="100%" height={340} minHeight={300}>
                        <BarChart
                          data={analyticsData.monthlySales}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip 
                            formatter={(value) => ['GHC' + value, 'Sales']}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                          />
                          <Bar dataKey="value" fill="#FF6C6C" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute w-32 h-32 rounded-full bg-[#FF6C6C]/10 -right-6 -top-6"></div>
                <div className="absolute w-24 h-24 rounded-full bg-[#FFD166]/10 -left-4 -bottom-4"></div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Overview */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Real Results for Your Business</h2>
              <p className="text-lg text-gray-600">
                Our vendors see significant improvements in key business metrics
              </p>
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <StatCard 
              title="Average Revenue Increase" 
              value="43%" 
              icon={DollarSign} 
              color="bg-[#FF6C6C]"
              footnote="for vendors in their first year"
            />
            <StatCard 
              title="New Customer Acquisition" 
              value="2.8x" 
              icon={Users}
              color="bg-[#FFA26C]" 
              footnote="compared to traditional marketing"
            />
            <StatCard 
              title="Conversion Rate" 
              value="24.8%" 
              icon={TrendingUp} 
              color="bg-[#06D6A0]"
              footnote="industry-leading performance"
            />
            <StatCard 
              title="ROI" 
              value="312%" 
              icon={Target} 
              color="bg-[#118AB2]"
              footnote="average return on platform investment"
            />
          </div>
        </div>
      </section>
      
      {/* Analytics Dashboard Preview */}
      <section className="py-20 bg-gray-50">
        <div className="container px-4 mx-auto">
          <div className="mx-auto mb-16 max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Powerful Analytics at Your Fingertips</h2>
              <p className="text-lg text-gray-600">
                Comprehensive dashboards provide clear insights into every aspect of your business performance
              </p>
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative mx-auto max-w-7xl"
          >
            <div className="overflow-hidden relative z-10 bg-white rounded-xl shadow-xl">
              <div className="p-6 pt-10 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold">Dashboard Overview</h3>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
                <div className="h-[270px]">
                  <h4 className="mb-4 font-semibold">Revenue Trend</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart
                      data={analyticsData.monthlySales}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#FF6C6C" 
                        fill="url(#colorValue)" 
                      />
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF6C6C" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#FF6C6C" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="h-[270px]">
                  <h4 className="mb-4 font-semibold">Category Distribution</h4>
                  <div className="flex items-center h-full">
                    <ResponsiveContainer width="60%" height={200}>
                      <PieChart>
                        <Pie
                          data={analyticsData.categoryDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {analyticsData.categoryDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="w-40">
                      {analyticsData.categoryDistribution.map((entry, index) => (
                        <div key={index} className="flex items-center mb-2">
                          <div 
                            className="mr-2 w-3 h-3 rounded-full" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="text-sm text-gray-600">{entry.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
              
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute w-64 h-64 rounded-full bg-[#FF6C6C]/5 -right-12 -bottom-12 z-0"></div>
            <div className="absolute w-48 h-48 rounded-full bg-[#FFD166]/5 -left-8 -top-8 z-0"></div>
          </motion.div>
        </div>
      </section>
      
      {/* Features */}
      <section className="py-20 bg-white">
        <div className="container px-4 mx-auto">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Analytics Features That Drive Growth</h2>
              <p className="text-lg text-gray-600">
                Comprehensive tools to help you understand your data and make informed decisions
              </p>
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {analyticsFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white rounded-xl border border-gray-100 transition-all hover:shadow-lg"
              >
                <div className="p-4 mb-4 bg-gray-50 rounded-lg">
                  {feature.icon}
                </div>
                <h3 className="mb-3 text-xl font-bold">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F6C] text-white">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-6 text-3xl font-bold md:text-4xl">Transform Your Business with Data-Driven Insights</h2>
              <p className="mb-8 text-lg">
                Start turning your data into actionable insights that drive growth, engagement, and revenue.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <motion.button
                onClick={()=>navigate("/signin")}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 font-medium text-[#FF6C6C] transition-all bg-white rounded-lg hover:shadow-lg"
                >
                  Get Started Free
                </motion.button>
                <button onClick={()=>navigate("/signin")} className="px-8 py-4 font-medium bg-white bg-opacity-20 rounded-lg transition-all hover:bg-opacity-30">
                  Contact Us
                </button>
              </div>
              <p className="mt-6 text-sm text-white text-opacity-80">Great subscription Packages for your Business</p>
            </motion.div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, footnote }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="p-6 bg-white rounded-xl shadow-sm"
  >
    <div className="flex justify-between items-start">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <h3 className="mt-1 text-3xl font-bold">{value}</h3>
        {footnote && <p className="mt-1 text-xs text-gray-500">{footnote}</p>}
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </motion.div>
);

export default AnalyticsPage; 